import React, { useState } from 'react';
import axios from 'axios';

// Ledger maintenance, from the browser.
//
// This exists because a cPanel plan without Terminal has no way to run a script, and the rebuild is
// the one operation that has to be run by hand after a derivation change. Doing it here is also
// safer than the CLI: the server stops its own tick sessions for the duration and starts them cold
// afterwards, instead of relying on someone remembering to down the whole app first.
//
// Dry run is the default and the apply button stays out of reach until a dry run has been read,
// because this rewrites a published record.

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });
const money = (value) => `${value >= 0 ? '+' : ''}${Number(value).toFixed(2)}`;

function LedgerRebuildPanel() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [dump, setDump] = useState('');
  const [copied, setCopied] = useState(false);

  const call = async (apply) => {
    setBusy(apply ? 'apply' : 'dry');
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/admin/signals/rebuild-ledger`, { apply }, { headers: headers() },
      );
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  // Read-only. Produces the rows behind the trades table, the chart and the virtual account, so a
  // disagreement between the three can be read off the data instead of guessed at from a screenshot.
  const runForensics = async () => {
    setBusy('dump');
    setError('');
    setCopied(false);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/signals/forensics`, {}, { headers: headers() },
      );
      setDump(JSON.stringify(data, null, 2));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const rows = report?.results || [];
  // A tick-driven deployment is reported and left alone: re-deriving it from bars would replace
  // what its session actually recorded with a backtest of the same window.
  const skipped = rows.filter((row) => row.skipped === 'tick-driven');
  const disagreeing = rows.filter((row) => (
    row.skipped !== 'tick-driven' && Math.abs(row.before.ledger - row.before.account) >= 0.01
  ));

  return (
    <div className="section-card">
      <h2>Ledger maintenance</h2>
      <p className="muted-text">
        Re-derives each running signal&apos;s trades and equity curve from a single run, so the two
        agree. Use it after a change to how signals are derived — it is the browser equivalent of
        <code> scripts/signals/rebuildLedger.js</code>, for hosts with no terminal.
      </p>
      <p className="muted-text">
        Open positions, sent notifications, and the event log are left alone. Tick sessions are
        stopped for the duration and restarted cold, so nothing needs shutting down by hand.
      </p>
      <p className="muted-text">
        Signals driven by a tick session are <strong>skipped</strong>. This re-derives from closed
        bars, which is how the bar runner works but not how a tick session does — running it on one
        would throw away what the session recorded and publish a backtest in its place. A
        tick-driven signal whose record is wrong has to be redeployed instead.
      </p>

      {error && <p className="signal-error">{error}</p>}

      <div className="inline-actions">
        <button type="button" className="ghost-button compact-button" disabled={!!busy} onClick={() => call(false)}>
          {busy === 'dry' ? 'Checking…' : 'Dry run'}
        </button>
        <button type="button" className="ghost-button compact-button" disabled={!!busy} onClick={runForensics}>
          {busy === 'dump' ? 'Reading…' : 'Diagnostic dump'}
        </button>
        <button
          type="button"
          className="primary-cta compact-button"
          // Only after a dry run has been fetched and looked at: this rewrites published history.
          disabled={!!busy || !report || report.apply}
          onClick={() => {
            const count = disagreeing.length;
            const ok = window.confirm(
              `Rebuild ${report.results.length} running signal(s)?\n\n`
              + `${count} currently disagree with their own account and will change.\n\n`
              + 'Trade counts and net profit will drop to the truthful figures. This cannot be undone.',
            );
            if (ok) call(true);
          }}
        >
          {busy === 'apply' ? 'Rebuilding…' : 'Rebuild for real'}
        </button>
      </div>

      {dump && (
        <div className="rebuild-dump">
          <div className="inline-actions">
            <strong>Diagnostic dump</strong>
            <button
              type="button"
              className="ghost-button compact-button"
              onClick={() => {
                navigator.clipboard?.writeText(dump).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className="ghost-button compact-button" onClick={() => setDump('')}>
              Hide
            </button>
          </div>
          <p className="muted-text">
            Nothing was written. Paste this back for diagnosis — it carries no personal data, only
            deployment ids, trade rows and equity points.
          </p>
          <pre className="rebuild-dump-body">{dump}</pre>
        </div>
      )}

      {report && (
        <>
          <p className="muted-text">
            {report.apply
              ? `${report.reconciled}/${report.total} now reconcile.`
              : `Dry run — nothing written. ${disagreeing.length} of ${rows.length} disagree with their account.`}
            {skipped.length > 0 && ` ${skipped.length} skipped as tick-driven.`}
          </p>
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Signal</th>
                  <th>Trades</th>
                  <th>Ledger says</th>
                  <th>Account says</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {report.results.map((row) => {
                  const shown = row.after || row.before;
                  const agrees = Math.abs(shown.ledger - shown.account) < 0.01;
                  return (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        {row.after && row.before.trades !== row.after.trades && (
                          <span className="muted-text"> · {row.before.trades} → {row.after.trades}</span>
                        )}
                      </td>
                      <td>{shown.trades}</td>
                      <td>{money(shown.ledger)}</td>
                      <td>{money(shown.account)}</td>
                      <td>
                        {row.error
                          ? <span className="signal-pill danger">{row.error}</span>
                          : row.skipped === 'tick-driven'
                            ? <span className="signal-pill">Tick-driven — skipped</span>
                            : agrees
                              ? <span className="signal-pill ok">Agree</span>
                              : <span className="signal-pill warn">Disagree</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default LedgerRebuildPanel;
